import { Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { StyledDivider, StyledLink } from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ACCOUNT_CREATION_ROUTE,
  BUTTON,
  ENABLE_ACCOUNT_ROUTE,
  H1,
  H3,
  LOG_IN,
  SECONDARY_MAIN,
  STRETCH,
  VERIFY_ACCOUNT_ROUTE,
} from "../../Constants/CommonConstants";
import {
  ACCOUNT_BLOCKED,
  ACCOUNT_DEACTIVATED,
  ACCOUNT_DISABLED,
  ACCOUNT_LOCKED,
  DISABLED_DUE_TO_INACTIVITY,
  LOGIN_CHALLENGE,
  LOGIN_CREATE_ACCOUNT_LINK,
  LOGIN_HEADER,
  LOGIN_OR_DIVIDER,
} from "../../Constants/LoginConstants";
import { clearAccountCreationSlice } from "../../Features/AccountCreationSlice";
import { performLogout } from "../../Features/AuthorizationSlice";
import {
  clearLoginSlice,
  resetLoginResponseDescription,
} from "../../Features/LoginSlice";
import { clearProviderAccountCreationSlice } from "../../Features/ProviderAccountCreationSlice";
import { manageLoginSelector } from "../../Selectors/LoginSelector";
import {
  StyledCreateAccountLinkBox,
  StyledLoginFormBox,
  StyledLoginFormGrid,
  StyledLoginFormStack,
  StyledLoginIllustration,
  StyledLoginIllustrationStack,
  StyledLoginImageGrid,
} from "../../Styles/Login/LoginStyles";
import LoginForm from "./LoginForm";
import { CannotConfirmAccountModal } from "./Modals/CannotConfirmAccountModal";

/**
 * @description Login screen which lets the user to Login by providing  username and password
 * @returns {ReactNode} Renders the Login screen to the user
 */

const Login = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { loginResponseDescription, fpactUserRole } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const [cannotConfirmModalIsOpen, setCannotConfirmModalIsOpen] =
    useState(false);

  useEffect(() => {
    dispatch(clearAccountCreationSlice());
    dispatch(clearProviderAccountCreationSlice());
    dispatch(performLogout());
    dispatch(clearLoginSlice());
  }, [dispatch]);

  //handle navigation and modal display based on the loginAPI response
  useEffect(() => {
    switch (loginResponseDescription) {
      case LOGIN_CHALLENGE:
        navigate(VERIFY_ACCOUNT_ROUTE, {
          state: { previousScreen: LOG_IN },
        });
        dispatch(resetLoginResponseDescription());
        break;
      case ACCOUNT_LOCKED:
      case ACCOUNT_BLOCKED:
      case ACCOUNT_DEACTIVATED:
      case ACCOUNT_DISABLED:
        setCannotConfirmModalIsOpen(true);
        dispatch(resetLoginResponseDescription());
        break;
      case DISABLED_DUE_TO_INACTIVITY:
        navigate(ENABLE_ACCOUNT_ROUTE);
        dispatch(resetLoginResponseDescription());
        break;
      default:
        break;
    }
  }, [dispatch, navigate, loginResponseDescription, fpactUserRole]);

  const createAccountLinkClickHandler = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };

  return (
    <Grid container alignItems={STRETCH}>
      <StyledLoginFormGrid item xs={12} md={6}>
        <StyledLoginFormBox>
          <StyledLoginFormStack spacing={{ xs: 7, md: 5 }}>
            <Typography
              component={H1}
              variant={H3}
              color={SECONDARY_MAIN}
              id={LOGIN_HEADER}
            >
              {intl.formatMessage({ id: LOGIN_HEADER })}
            </Typography>
            <LoginForm />
            <Stack spacing={{ xs: 4, md: 5 }}>
              <StyledDivider id={LOGIN_OR_DIVIDER}>
                {intl.formatMessage({ id: LOGIN_OR_DIVIDER })}
              </StyledDivider>
              <StyledCreateAccountLinkBox>
                <StyledLink
                  id={LOGIN_CREATE_ACCOUNT_LINK}
                  onClick={createAccountLinkClickHandler}
                  component={BUTTON}
                  type={BUTTON}
                >
                  {intl.formatMessage({ id: LOGIN_CREATE_ACCOUNT_LINK })}
                </StyledLink>
              </StyledCreateAccountLinkBox>
            </Stack>
          </StyledLoginFormStack>
        </StyledLoginFormBox>
      </StyledLoginFormGrid>
      <StyledLoginImageGrid item xs={0} md={6}>
        <StyledLoginIllustrationStack>
          <StyledLoginIllustration />
        </StyledLoginIllustrationStack>
        <CannotConfirmAccountModal
          open={cannotConfirmModalIsOpen}
          setCannotConfirmModalIsOpen={setCannotConfirmModalIsOpen}
        />
      </StyledLoginImageGrid>
    </Grid>
  );
};

export default Login;
